/**
 * Offline Storage Service für Feld-Modus
 * 
 * Speichert Fotos und Sprachnotizen in IndexedDB wenn offline,
 * und synchronisiert sie automatisch wenn wieder online.
 */

const DB_NAME = 'FeldModusOfflineDB';
const DB_VERSION = 1;
const STORE_NAME = 'pendingUploads';

let db = null;

/**
 * Initialisiert die IndexedDB Datenbank
 */
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB Error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('✅ Offline-DB initialisiert');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        store.createIndex('type', 'type', { unique: false });
        store.createIndex('projectId', 'projectId', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
    };
  });
};

/**
 * Speichert einen Upload in der Offline-Warteschlange
 */
export const savePendingUpload = async ({
  type, // 'photo' | 'voice' | 'defect'
  blob,
  projectId,
  userId,
  userName,
  caption,
  description,
  metadata = {}
}) => {
  await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const upload = {
      type,
      blob,
      projectId,
      userId,
      userName,
      caption,
      description,
      metadata,
      status: 'pending', // 'pending' | 'uploading' | 'failed' | 'completed'
      retryCount: 0,
      createdAt: new Date().toISOString(),
      lastAttempt: null
    };
    
    const request = store.add(upload);
    
    request.onsuccess = () => {
      console.log('📥 Upload gespeichert für Offline-Sync:', request.result);
      resolve(request.result);
    };
    
    request.onerror = () => {
      console.error('Fehler beim Speichern:', request.error);
      reject(request.error);
    };
  });
};

/**
 * Holt alle ausstehenden Uploads
 */
export const getPendingUploads = async () => {
  await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.getAll('pending');
    
    request.onsuccess = () => {
      resolve(request.result || []);
    };
    
    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * Holt alle Uploads (auch fehlgeschlagene)
 */
export const getAllPendingUploads = async () => {
  await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    
    request.onsuccess = () => {
      const uploads = request.result || [];
      // Filter out completed uploads
      resolve(uploads.filter(u => u.status !== 'completed'));
    };
    
    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * Aktualisiert den Status eines Uploads
 */
export const updateUploadStatus = async (id, status, error = null) => {
  await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const upload = getRequest.result;
      if (upload) {
        upload.status = status;
        upload.lastAttempt = new Date().toISOString();
        if (error) upload.lastError = error;
        if (status === 'failed') upload.retryCount++;
        
        const putRequest = store.put(upload);
        putRequest.onsuccess = () => resolve(upload);
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        reject(new Error('Upload nicht gefunden'));
      }
    };
    
    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Löscht einen Upload aus der Warteschlange
 */
export const deleteUpload = async (id) => {
  await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);
    
    request.onsuccess = () => {
      console.log('🗑️ Upload gelöscht:', id);
      resolve();
    };
    
    request.onerror = () => reject(request.error);
  });
};

/**
 * Löscht alle abgeschlossenen Uploads
 */
export const clearCompletedUploads = async () => {
  await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.openCursor(IDBKeyRange.only('completed'));
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    
    request.onerror = () => reject(request.error);
  });
};

/**
 * Zählt ausstehende Uploads
 */
export const countPendingUploads = async () => {
  const uploads = await getAllPendingUploads();
  return uploads.length;
};

/**
 * Prüft ob online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Erstellt eine Blob-URL für Vorschau
 */
export const createPreviewUrl = (blob) => {
  if (blob instanceof Blob) {
    return URL.createObjectURL(blob);
  }
  return null;
};

/**
 * Gibt eine Blob-URL frei
 */
export const revokePreviewUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};
